/**
 * Video frames: the stills Stage 1 takes from a clip and keeps as photos (`fromVideo`, the clip's `videoId`, `timestamp` in
 * seconds; Stage1DataEntry buildFramePhotos and project-video-evidence-preparation). They stay photos for the pipeline, the
 * preview, markup and the quote. The Job cockpit counts them apart from the files the customer sent and lists them under
 * the video they came from (Lucid 016 O1, Peter 2026-10-28: "22 reference files (3 videos) ... inflating the reference
 * files count").
 */

export function isVideoFrame(photo: unknown): boolean {
    return !!photo && typeof photo === 'object' && !!(photo as { fromVideo?: unknown }).fromVideo;
}

function videoKey(value: unknown): string {
    return value == null ? '' : String(value);
}

/**
 * The file the customer sent that a video row stands for. A part of a split video (stage1-video-split-tracking.ts)
 * stands for its original, so a walkthrough cut into four parts is one video sent. The same key decides how many
 * selected videos a folder import prepared (preprocessed-row-builder.js preparedFileCounts).
 */
export function sentVideoKey(video: unknown): string {
    const row = (video && typeof video === 'object' ? video : {}) as { id?: unknown; quoteMakerFileId?: unknown; splitSource?: { sourcePath?: unknown; size?: unknown } | null };
    if (!row.splitSource) return `row:${videoKey(row.id)}`;
    if (row.quoteMakerFileId) return `source:${videoKey(row.quoteMakerFileId)}`;
    // A dropped clip's source path is its bare name: two phones' IMG_0001.MOV are two videos, told apart by size.
    return `source:${videoKey(row.splitSource.sourcePath)}:${videoKey(row.splitSource.size)}`;
}

/** How many videos the customer sent: the listed rows, a split video's parts counted once. */
export function countSentVideos(videos: unknown): number {
    const list = Array.isArray(videos) ? videos : [];
    // A row with no id is still a video of its own.
    return new Set(list.map((video, index) => {
        const key = sentVideoKey(video);
        return key === 'row:' ? `row-index:${index}` : key;
    })).size;
}

export interface VideoFrameCount {
    frames: number;
    /** The clips the frames came from; a frame with no clip id is counted but names no clip. */
    videos: number;
}

/** The frames and the clips they came from. Given the listed videos, a split video's parts are one clip (sentVideoKey). */
export function countVideoFrames(photos: unknown, videos?: unknown): VideoFrameCount {
    const list = Array.isArray(photos) ? photos : [];
    const sentKeyById = new Map<string, string>();
    for (const video of Array.isArray(videos) ? videos : []) {
        const id = videoKey(video?.id);
        if (id) sentKeyById.set(id, sentVideoKey(video));
    }
    const clips = new Set<string>();
    let frames = 0;
    for (const photo of list) {
        if (!isVideoFrame(photo)) continue;
        frames += 1;
        const key = videoKey((photo as { videoId?: unknown }).videoId);
        if (key) clips.add(sentKeyById.get(key) || `row:${key}`);
    }
    return { frames, videos: clips.size };
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** "11 frames from 3 videos", "1 frame", or '' when there are none. */
export function videoFramesPhrase({ frames, videos }: VideoFrameCount): string {
    if (frames <= 0) return '';
    return videos > 0 ? `${plural(frames, 'frame')} from ${plural(videos, 'video')}` : plural(frames, 'frame');
}

/** Where in its clip a frame was taken, "0:42" or "1:02:05"; '' when the frame has no time. */
export function frameTimeLabel(seconds: unknown): string {
    if (seconds === null || seconds === undefined || seconds === '') return '';
    const value = Number(seconds);
    if (!Number.isFinite(value) || value < 0) return '';
    const whole = Math.floor(value);
    const hours = Math.floor(whole / 3600);
    const minutes = Math.floor((whole % 3600) / 60);
    const secs = String(whole % 60).padStart(2, '0');
    return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${secs}` : `${minutes}:${secs}`;
}

/**
 * The frames taken from this clip, in the photos' order. Removing a video removes these with it (Peter 2026-10-28). A clip
 * with no id owns no frames, so a frame with no clip id is never caught.
 */
export function framesOfVideo(photos: unknown, video: unknown): any[] {
    const key = videoKey((video as { id?: unknown } | null | undefined)?.id);
    if (!key || !Array.isArray(photos)) return [];
    return photos.filter(photo => isVideoFrame(photo) && videoKey(photo.videoId) === key);
}

export interface VideoFrameGroups {
    /** Indexes of the photos the Photos grid shows: the customer's photos, and frames whose clip is not in the list. */
    loose: number[];
    /** Each listed clip's frames, by the clip's id, as indexes into the photos, in the photos' order. */
    byVideo: Map<string, number[]>;
}

/**
 * Splits the photos into the Photos grid's and each listed video's frames. A frame whose clip is not listed (the video was
 * removed, or another surface passes no videos) stays in the grid, so no photo ever drops out of sight.
 */
export function groupFramesByVideo(photos: unknown, videos: unknown): VideoFrameGroups {
    const list = Array.isArray(photos) ? photos : [];
    const clipIds = new Set((Array.isArray(videos) ? videos : []).map(video => videoKey(video?.id)).filter(Boolean));
    const loose: number[] = [];
    const byVideo = new Map<string, number[]>();
    list.forEach((photo, index) => {
        const key = isVideoFrame(photo) ? videoKey(photo.videoId) : '';
        if (!key || !clipIds.has(key)) {
            loose.push(index);
            return;
        }
        const frames = byVideo.get(key);
        if (frames) frames.push(index);
        else byVideo.set(key, [index]);
    });
    return { loose, byVideo };
}
